// Angular imports
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core'

// Third-party imports
import { Store } from '@ngrx/store'
import { ToastrService } from 'ngx-toastr'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

// Project-specific imports
import { DigitalSpecialistSession } from '@cwan-gpt-ui/models'
import { FileUploadService } from '@cwan-gpt-ui/services'
import {
  clearTemporaryFiles,
  RootState,
  selectActiveSession,
  selectTemporaryFiles,
  setTemporaryFiles,
} from '@cwan-gpt-ui/state-management'

@Component({
  selector: 'temp-file-upload',
  templateUrl: './temp-file-upload.component.html',
  styleUrls: ['./temp-file-upload.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class TempFileUploadComponent implements OnInit, OnDestroy {
  constructor(
    private readonly fileUploadService: FileUploadService,
    private readonly toastr: ToastrService,
    private readonly store: Store<RootState>
  ) {}
  selectedFile?: string

  disableButton = false
  showProgress = false
  trackProgressTimeout = 0
  timeout = 2000
  uploadStatusErrorCount = 0
  private readonly destroyed$ = new Subject<void>()
  private session?: DigitalSpecialistSession

  resetForm() {
    this.disableButton = false
    this.showProgress = false
  }

  ngOnInit(): void {
    this.store
      .select(selectActiveSession)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((session) => {
        this.session = session
      })

    this.store
      .select(selectTemporaryFiles)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((files) => {
        this.selectedFile = files[this.session?.userId || '']?.[0]
      })
  }

  cancelFileSelection() {
    this.selectedFile = undefined
    this.store.dispatch(clearTemporaryFiles({ id: this.session?.userId || '' }))
    const fileInput: HTMLInputElement = document.getElementById(
      'file-upload'
    ) as HTMLInputElement
    if (fileInput) {
      fileInput.value = '' // Clear the input value
    }
  }

  async onChange(event: Event) {
    const input = event.target as HTMLInputElement
    if (!input?.files?.length) {
      return
    }
    const selectedFile = input.files[0]

    // check if file is locked before attempting upload
    const isAccessible = await this.isFileAccessible(selectedFile)
    if (!isAccessible) {
      this.toastr.error(
        'The selected file appears to be currently in use. Please close it and try uploading again.'
      )
      this.cancelFileSelection()
      this.disableButton = false
      this.showProgress = false
      return
    }

    try {
      // clear previous upload state
      clearTimeout(this.trackProgressTimeout)
      this.disableButton = true
      this.showProgress = true

      // prepare form data for upload
      const fileName = selectedFile.name.replace(/[|+]/g, '_')
      const formData = new FormData()
      formData.append(
        'file',
        selectedFile,
        this.changeFileExtensionToLowercase(fileName)
      )

      const response = await this.fileUploadService.uploadFile(
        formData,
        this.session?.sessionId
      )

      if (response?.jobId) {
        await this.trackUploadProgress(response.jobId, fileName)
      }
    } catch (error) {
      this.toastr.error('File Upload Failed, please retry again.')
      console.error('Failed to upload file:', error)
      // clean up the file state
      this.cancelFileSelection()
      this.disableButton = false
      this.showProgress = false
    }
  }

  async trackUploadProgress(jobId: string, fileName: string) {
    try {
      const status = await this.fileUploadService.getUploadStatus(jobId)
      this.uploadStatusErrorCount = 0
      if (status.stage === 'ERROR') {
        this.resetForm()
        this.toastr.error('File Upload Failed, please retry again.')
        await this.fileUploadService.deleteFile(fileName)
      } else if (status.percent_complete === 100) {
        setTimeout(() => {
          this.resetForm()
        }, 2000)

        this.toastr.success('File Uploaded successfully')
        this.store.dispatch(
          setTemporaryFiles({
            filenames: [fileName],
            sessionId: this.session?.userId || '',
          })
        )
        this.disableButton = false
      } else {
        this.trackProgressTimeout = setTimeout(() => {
          this.timeout = Math.min(7000, this.timeout * 1.25)
          this.trackUploadProgress(jobId, fileName)
        }, this.timeout)
      }
    } catch (error) {
      console.error(error)
      if (++this.uploadStatusErrorCount < 4) {
        this.trackProgressTimeout = setTimeout(() => {
          this.trackUploadProgress(jobId, fileName)
        }, 2000 * this.uploadStatusErrorCount)
      } else {
        this.resetForm()
        this.toastr.error('File Upload Failed, please retry again.')
        await this.fileUploadService.deleteFile(fileName)
      }
    }
  }

  private changeFileExtensionToLowercase(filename: string) {
    const parts = filename.split('.')
    const extension = parts.pop()?.toLowerCase()
    return `${parts.join('.')}.${extension}`
  }

  // adding a check to see if the file is in use - it prevents windows users to upload files if the file is in 'locked' state
  private async isFileAccessible(file: File): Promise<boolean> {
    try {
      const stream = file.stream() // when a file is locked by Windows, attempting to create a stream will throw an error
      await stream.cancel()
      return true
    } catch (error) {
      console.log('File accessibility check failed:', error)
      return false
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.trackProgressTimeout)
    this.destroyed$.next()
    this.destroyed$.complete()
  }
}
